/**
 * @file app/helpers/invoiceDetails.js
 *
 * Single source of truth for "does this order need a (VAT) invoice?" and for
 * extracting a normalized set of invoice billing details out of an order.
 *
 * Two payload shapes carry invoice data on an order:
 *   1. `order.invoiceData`            — POS / backend shape:
 *        { company: { nev, adoszam, cim }, firstName, lastName, email, phone,
 *          address, invoiceNumber }
 *   2. `order.userInfo.vatInvoice`    — online (web/app) shape:
 *        { billingCompanyName, taxNumber, billingAddress: { zip, city, address, other } }
 *
 * Business rule: when an order needs a VAT invoice the customer is billed via a
 * NAV invoice (issued by the backend), so the order must **never** be sent to a
 * fiscal cash register / receipt printer. {@link orderNeedsInvoice} is the gate
 * used to enforce that, and {@link getInvoiceDetails} feeds both the order
 * details alert and the printed receipt block.
 */

/**
 * Format an address that may be a plain string or a structured object.
 * @param {string|{zip?:string,city?:string,address?:string,other?:string}} addr
 * @returns {string}
 */
const formatInvoiceAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr.trim();
  const parts = [
    [addr.zip, addr.city].filter((p) => p && String(p).trim()).join(' '),
    addr.address,
    addr.other && addr.other.info ? addr.other.info : addr.other,
  ].filter((p) => p && String(p).trim());
  return parts.join(', ');
};

/**
 * Whether the given order requires a VAT invoice (and therefore must NOT be
 * pushed to a fiscal cash register).
 * @param {Object} order
 * @returns {boolean}
 */
export const orderNeedsInvoice = (order = {}) => {
  if (!order || typeof order !== 'object') return false;
  if (order.needInvoice === true) return true;

  const inv = order.invoiceData;
  if (inv && typeof inv === 'object') {
    if (inv.company && (inv.company.nev || inv.company.adoszam)) return true;
    if (inv.firstName || inv.lastName || inv.email || inv.invoiceNumber) return true;
  }

  if (order.userInfo && order.userInfo.vatInvoice) return true;

  return false;
};

/**
 * Extract a normalized invoice details object from an order, or `null` when no
 * invoice data is present.
 * @param {Object} order
 * @returns {null|{companyName:string,taxNumber:string,address:string,name:string,email:string,phone:string,invoiceNumber:string}}
 */
export const getInvoiceDetails = (order = {}) => {
  if (!order || typeof order !== 'object') return null;

  const vat = order?.userInfo?.vatInvoice;
  if (vat) {
    return {
      companyName: vat.billingCompanyName || '',
      taxNumber: vat.taxNumber || '',
      address: formatInvoiceAddress(vat.billingAddress),
      name: '',
      email: vat.email || '',
      phone: vat.phone || '',
      invoiceNumber: '',
    };
  }

  const inv = order.invoiceData;
  if (!inv || typeof inv !== 'object') {
    return order.needInvoice === true
      ? { companyName: '', taxNumber: '', address: '', name: '', email: '', phone: '', invoiceNumber: '' }
      : null;
  }

  const company = inv.company || null;
  const name = [inv.lastName, inv.firstName]
    .filter((p) => p && String(p).trim())
    .join(' ')
    .trim();

  return {
    companyName: company?.nev || '',
    taxNumber: company?.adoszam || '',
    address: company?.cim || formatInvoiceAddress(inv.address),
    name,
    email: inv.email || '',
    phone: inv.phone || '',
    invoiceNumber: inv.invoiceNumber || '',
  };
};

export default { orderNeedsInvoice, getInvoiceDetails };
